import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApp } from '../context/AppContext'
import PhoneAuth from '../components/auth/PhoneAuth'
import OTPVerification from '../components/auth/OTPVerification'
import UserDetailsForm from '../components/user/UserDetailsForm'
import FaceScanMediaPipe from '../components/scan/FaceScanMediaPipe'
import { Phone, Shield, User, Camera } from 'lucide-react'

function ScanPage() {
  const navigate = useNavigate()
  const { isAuthenticated, user, verifyAuth } = useApp()
  const [currentStep, setCurrentStep] = useState('phone')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isInitializing, setIsInitializing] = useState(true)

  useEffect(() => {
    const initializeAuth = async () => {
      if (isAuthenticated) {
        // User is already authenticated, check if profile is complete
        if (user && user.name && user.age && user.gender && user.height_cm && user.weight_kg) {
          // User has complete profile - allow them to proceed to scan
          // (They came here explicitly, so let them scan)
          setCurrentStep('scan')
        } else {
          setCurrentStep('profile')
        }
      } else {
        // Try to verify existing token
        const hasValidToken = await verifyAuth()
        if (hasValidToken && user && user.name && user.age && user.gender && user.height_cm && user.weight_kg) {
          // User has complete profile - allow them to proceed to scan
          // (They came here explicitly, so let them scan)
          setCurrentStep('scan')
        } else if (hasValidToken) {
          setCurrentStep('profile')
        } else {
          setCurrentStep('phone')
        }
      }
      setIsInitializing(false)
    }

    // Only run on initial mount, not on every state change
    if (isInitializing) {
      initializeAuth()
    }
  }, []) // Empty dependency array - only run once

  const handlePhoneSubmit = (phone) => {
    setPhoneNumber(phone)
    setCurrentStep('otp')
  }

  const handleOTPSubmit = () => {
    setCurrentStep('profile')
  }

  const handleProfileComplete = () => {
    // Check if there's a redirect destination stored (e.g., user came from HRA page)
    const redirectAfterAuth = sessionStorage.getItem('redirectAfterAuth')
    if (redirectAfterAuth) {
      sessionStorage.removeItem('redirectAfterAuth')
      navigate(redirectAfterAuth)
    } else {
      // Redirect to home page after profile completion so user can choose between Face Scan and HRA
      navigate('/')
    }
  }

  const handleBackToPhone = () => {
    setCurrentStep('phone')
  }

  const handleScanComplete = (scanId) => {
    console.log('handleScanComplete called with scanId:', scanId)
    console.log('Navigating to:', `/report/${scanId}`)
    navigate(`/report/${scanId}`)
  }

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-3">
          <h1 className="text-lg font-bold text-gray-900 mb-1">Health Scan</h1>
          <p className="text-gray-600 text-xs">Get your personalized health analysis in minutes</p>
        </div>

        {/* Progress Indicator - Single Row */}
        <div className="w-full max-w-3xl mx-auto mb-4 px-4">
          <div className="flex items-center justify-between relative">
            {/* Progress Line */}
            <div className="absolute top-2 left-0 right-0 h-0.5 bg-gray-200 rounded-full -z-10">
              <div 
                className="h-full bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full transition-all duration-500 ease-out"
                style={{ 
                  width: `${(['phone', 'otp', 'profile', 'scan'].indexOf(currentStep) / 3) * 100}%` 
                }}
              />
            </div>
            
            {[
              { key: 'phone', label: 'Phone', icon: Phone, description: 'Enter your number' },
              { key: 'otp', label: 'Verify', icon: Shield, description: 'Confirm with OTP' },
              { key: 'profile', label: 'Profile', icon: User, description: 'Basic info' },
              { key: 'scan', label: 'Scan', icon: Camera, description: 'Take photo' }
            ].map((step, index) => {
              const isActive = step.key === currentStep
              const isCompleted = ['phone', 'otp', 'profile', 'scan'].indexOf(currentStep) > ['phone', 'otp', 'profile', 'scan'].indexOf(step.key)
              
              return (
                <div key={step.key} className="flex items-center space-x-2 relative z-10 flex-shrink-0">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shadow-sm transition-all duration-300 ${
                    isActive 
                      ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white scale-105 shadow-primary-600/30' 
                      : isCompleted 
                        ? 'bg-success text-white shadow-success/30' 
                        : 'bg-white text-gray-400 border border-gray-300 shadow-sm'
                  }`}>
                    {isCompleted ? (
                      <span className="text-xs font-bold">✓</span>
                    ) : (
                      <step.icon className="w-3 h-3" />
                    )}
                  </div>
                  <div className="text-left">
                    <span className={`text-xs font-semibold ${
                      isActive ? 'text-primary-600' : isCompleted ? 'text-success' : 'text-gray-500'
                    }`}>
                      {step.label}
                    </span>
                    <span className={`text-xs ml-1 hidden md:inline ${
                      isActive ? 'text-primary-600/70' : isCompleted ? 'text-success/70' : 'text-gray-400'
                    }`}>
                      {step.description}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Step Content */}
        <div>
          {currentStep === 'phone' && (
            <PhoneAuth onPhoneSubmit={handlePhoneSubmit} />
          )}

          {currentStep === 'otp' && (
            <OTPVerification 
              phoneNumber={phoneNumber} 
              onBack={handleBackToPhone}
              onOTPSubmit={handleOTPSubmit}
            />
          )}

          {currentStep === 'profile' && (
            <UserDetailsForm onComplete={handleProfileComplete} />
          )}

          {currentStep === 'scan' && (
            <FaceScanMediaPipe onScanComplete={handleScanComplete} />
          )}
        </div>

        {/* Help Section - Compact */}
        <div className="mt-4 max-w-md mx-auto">
          <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">
              Quick Tips
            </h3>
            <div className="space-y-1 text-xs text-gray-600">
              <div className="flex items-start">
                <span className="w-1 h-1 bg-primary-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                <p><strong>Photo:</strong> Good lighting, face centered, looking at camera</p>
              </div>
              <div className="flex items-start">
                <span className="w-1 h-1 bg-primary-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                <p><strong>Privacy:</strong> Images are processed securely and deleted after analysis</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ScanPage
